<template>
  <div class="ui zw subscription bottom active tab segment"
       v-if="showPricing || showTransactions || showAccessories || showPaymentMethod">
    <!-- Subscriptions content -->
    <div class="topper">
      <div class="text">
        {{ activeSubscription.product.name }}
        <div class="water_meter_block" v-if="$store.getters.activeMerchant.kamstrup && activeSubscription.product.category.water_meter">
          <template v-if="activeSubscription.kamstrup_meters.length > 0">
            (<span class="water_meter_notice" :class="activeSubscription.status === 'overdue' ? 'overdue': ''">{{ meterReadingNotice }}</span>)
          </template>
          <template v-else>
            (<span class="water_meter_notice">{{ $t('pages.dashboard.details.kamstrup.payment.no_data')}}</span>)
          </template>
        </div>
        <div class="delayed-block" v-if="isDelayedSubscription">
          (<span class="delayed">{{ $t('globals.status.delayed') }}</span>) -
          <span class="activation-date">{{ $t('pages.dashboard.subscriptions.activation_on') }} {{ startDate }}</span>
        </div>
        <div v-if="activeSubscription.is_free" style="display: inline-block;">
          (<span style="color: green;">{{ $t('globals.status.free') }}</span>)
        </div>
      </div>

      <button class="ui right floated blue button"
              v-if="canPayOverdueFee"
              :disabled="isButtonDisabled || activeSubscription.overdue_fee_payed ? 'disabled' : false"
              @click="showPayOverdueFeeModal = !showPayOverdueFeeModal">
              <span v-if="activeSubscription.overdue_fee_payed">
                {{ $t('globals.buttons.overdue_fee_paid') }}
              </span>
        <span v-else>
                {{ $t('globals.buttons.pay_overdue_fee') }}
              </span>
      </button>
      <button class="ui right floated blue button"
              v-if="canPayOverdue"
              :disabled="isButtonDisabled || (activeSubscription.apply_overdue_fee && activeSubscription.product.overdue_fee_amount !== null && !activeSubscription.overdue_fee_payed) ? 'disabled' : false"
              @click="showPayOverdueModal = !showPayOverdueModal">
        {{ $t('globals.buttons.pay_overdue_payment') }}
      </button>
      <div class="ui right floated blue button change-payment-button" @click="showUpdatePaymentCardConfirmationModal = !showUpdatePaymentCardConfirmationModal"
           v-if="canChangePaymentMethod">{{ $t('globals.buttons.change_card') }}
      </div>
      <div class="ui right floated blue button change-payment-button" @click="openAddPaymentMethodModal"
           v-if="canAddPaymentMethod">{{ $t('globals.buttons.add_payment_method') }}
      </div>
      <div class="ui right floated grey button" @click="showConfirmationModal = !showConfirmationModal"
           v-if="canBeCancelled">{{ $t('globals.buttons.cancel_subscription') }}
      </div>
    </div>

    <div class="ui positive message" v-if="finalWaterPeriodPayment">
      <i class="check circle icon"></i>
      {{ $t('pages.dashboard.subscriptions.final_period_paid', {
        start: finalWaterPeriodPayment.period.start_date,
        end: finalWaterPeriodPayment.period.end_date
      }) }}
    </div>

    <div class="ui information two column padded stackable grid">
      <div class="row">
        <div class="sixteen wide mobile seven wide tablet six wide computer column" v-if="showPricing">
          <div class="ui basic fluid zw flex segment">
            <div class="ui divider horizontal header">
              {{ $t('pages.dashboard.subscriptions.paymentinterval') }}
            </div>
            <div class="ui zw one flex-center cards">
              <active-payment-plan :activeSubscription="activeSubscription"/>
            </div>
          </div>
        </div>
        <div class="sixteen wide mobile nine wide tablet ten wide computer column"
             v-if="showTransactions && !activeSubscription.is_free">
          <div class="ui basic zw flex segment">
            <div class="ui divider horizontal header">{{ $t('pages.dashboard.subscriptions.upcomingtrans') }}</div>
            <div class="ui segments">
              <upcoming-transaction :subscriptionid="activeSubscription.id"/>
            </div>
          </div>
        </div>
        <div class="sixteen wide mobile nine wide tablet ten wide computer column"
             v-else-if="processedTransactions.data.length > 0">
          <div class="ui basic zw flex segment">
            <div class="ui divider horizontal header">{{ $t('pages.dashboard.subscriptions.processedpayment') }}</div>
            <div class="ui segments">
              <transaction v-for="processedTransaction in processedTransactions.data"
                           :transaction="processedTransaction" :key="processedTransaction.id"
                           :amount="processedTransaction.amount"/>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="seven wide tablet six wide computer column" v-if="showPaymentMethod">
          <div class="ui divider horizontal header">{{ $t('pages.dashboard.subscriptions.paymethod') }}</div>
          <div class="ui zw one cards">
            <template v-if="activeSubscription.payment_method === 'nets' && activeSubscription.nets_details !== null">
              <card :card="activeSubscription.nets_details.payment_details" class="centered"/>
            </template>
            <template v-else-if="activeSubscription.payment_method === 'mobile_pay'">
              <card :card="null" :card-type="'mobile_pay'" class="centered"/>
            </template>
            <template v-else-if="activeSubscription.payment_method === 'vipps'">
              <card :card="null" :card-type="'vipps'" class="centered"/>
            </template>
          </div>
        </div>
        <div class="nine wide tablet ten wide computer column" v-if="showAccessories">
          <div class="ui divider horizontal header">{{ $t('pages.dashboard.subscriptions.accessories') }}</div>
          <div class="ui zw flex-center cards">
            <accessory :accessory="accessory" :changeable="false" v-for="accessory in activeAccessories"
                       :key="accessory.id"/>
          </div>
        </div>
      </div>
    </div>

    <modal ref="modal" :active="showUpdatePaymentCardModal && isOverdueChargedFailed"
           :title="$t('modals.update_payment_card.title')" icons="big alarm" @acceptmodal="updatePaymentInformation"
           @denymodal="closeUpdatePaymentCardModal">
      <h4>{{ $t('modals.update_payment_card.header') }}</h4>
      <span v-html="$t('modals.update_payment_card.body')"></span>
    </modal>

    <semantic-modal :active="showPayOverdueModal" v-if="activeSubscription.status === 'overdue'" class="small"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header">
        <h1>{{ $t('modals.overdue_payment_notice', {amount: displayPricingAmount}) }}</h1>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button">{{ $t('globals.buttons.no') }}</div>
        <div class="ui green ok button" @click="payOverdue">{{ $t('globals.buttons.yes') }}</div>
      </div>
    </semantic-modal>

    <semantic-modal :active="showPayOverdueFeeModal" v-if="activeSubscription.status === 'overdue'" class="small"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header" v-if="activeSubscription.payment_method === 'nets'">
        <h1 v-if="activeSubscription.nets_details.unscheduled_subscription_id">
          {{ $t('modals.overdue_fee.unscheduled', {amount: activeSubscription.product.overdue_fee_amount}) }}
        </h1>
        <h1 v-else>
          {{ $t('modals.nets_notice') }}
        </h1>
      </div>
      <div class="header" slot="header" v-if="activeSubscription.payment_method === 'mobile_pay'">
        <h1>{{ $t('modals.mobile_pay_notice') }}</h1>
      </div>
      <div class="header" slot="header" v-if="activeSubscription.payment_method === 'vipps'">
        <h1>{{ $t('modals.vipps_notice') }}</h1>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button">{{ $t('globals.buttons.no') }}</div>
        <div class="ui green ok button" @click="payOverdueFee">{{ $t('globals.buttons.yes') }}</div>
      </div>
    </semantic-modal>

    <semantic-modal :active="showUpdatePaymentCardConfirmationModal"
                    class="small"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header">
        <h1>{{ $t('pages.dashboard.subscriptions.validation.are_you_sure_change_card', {product_name: activeSubscription.product.name}) }}</h1>
      </div>

      <div class="modal-body-text">
        <p class="modal-body-text__lead">
          {{ $t('pages.dashboard.subscriptions.validation.are_you_sure_change_card_body_text_lead', {product_name: activeSubscription.product.name}) }}
        </p>
        <p class="modal-body-text__note">
          <i class="exclamation triangle icon modal-body-text__icon"></i>
          <span>{{ $t('pages.dashboard.subscriptions.validation.are_you_sure_change_card_body_text_note') }}</span>
        </p>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button">{{ $t('globals.buttons.no') }}</div>
        <div class="ui red ok button" @click="updatePaymentInformation">{{ $t('globals.buttons.yes') }}</div>
      </div>
    </semantic-modal>

    <semantic-modal v-model="showAddPaymentMethodModal"
                    class="medium"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header">
        <h1>{{ $t('pages.dashboard.subscriptions.add_payment_method.title') }}</h1>
      </div>

      <div class="modal-body-text">
        <p>{{ $t('pages.dashboard.subscriptions.add_payment_method.description') }}</p>
        <div class="ui zw two flex-center stackable cards">
          <payment-method v-for="paymentMethod in availablePaymentMethods"
                          :key="paymentMethod.id"
                          :id="paymentMethod.id"
                          :name="paymentMethod.name"
                          :slug="paymentMethod.slug"
                          :active="selectedPaymentMethod === paymentMethod.slug"
                          @select="selectedPaymentMethod = $event"
                          @deselect="selectedPaymentMethod = null"/>
        </div>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button" @click="showAddPaymentMethodModal = false">{{ $t('globals.buttons.cancel') }}</div>
        <div class="ui green button"
             :class="{ 'loading disabled': isAddingPaymentMethod, 'disabled': !selectedPaymentMethod }"
             @click="addPaymentMethod">{{ $t('globals.buttons.continue') }}</div>
      </div>
    </semantic-modal>

    <semantic-modal :active="showConfirmationModal"
                    v-if="activeSubscription.status === 'active' || activeSubscription.status === 'delayed'"
                    class="small"
                    :settings="{content_classes: {content: false}, closeable: true, closeable_button: true}">
      <div class="header" slot="header">
        <h1>{{ $t('pages.dashboard.subscriptions.validation.are_you_sure') }}</h1>
      </div>

      <div class="actions" slot="actions">
        <div class="ui deny button">{{ $t('globals.buttons.no') }}</div>
        <div class="ui red ok button" @click="cancelSubscription">{{ $t('globals.buttons.yes') }}</div>
      </div>
    </semantic-modal>

    <loading :active.sync="isLoading"
             :is-full-page="fullPage"/>
  </div>
</template>


<script>
import find from 'lodash/find'
import moment from 'moment'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

export default {
  name: 'Subscriptions',

  components: {
    Loading
  },

  props: ['subscriptions', 'activeSubscription'],

  data() {
    return {
      isLoading: false,
      fullPage: true,
      showPricing: false,
      showTransactions: false,
      processedTransactions: {
        data: []
      },
      successTransactions: [],
      showAccessories: false,
      showPaymentMethod: false,
      activeAccessories: [],
      showConfirmationModal: false,
      showUpdatePaymentCardConfirmationModal: false,
      showAddPaymentMethodModal: false,
      selectedPaymentMethod: null,
      isAddingPaymentMethod: false,
      showPayOverdueModal: false,
      showPayOverdueFeeModal: false,
      isOverdueChargedFailed: false,
      showUpdatePaymentCardModal: false,
      isDelayedSubscription: false,
      startDate: false
    }
  },

  watch: {
    activeSubscription: function () {
      this.activatePricing()
      this.activateTransactions()
      this.activatePaymentMethod()
      this.activateAccessories()
      this.findActiveAccessories()
      this.subscriptionDelayed()
      this.setStartDate()
    }
  },

  mounted() {
    this.activatePricing()
    this.activateTransactions()
    this.activatePaymentMethod()
    this.activateAccessories()
    this.findActiveAccessories()
    this.subscriptionDelayed()
    this.setStartDate()

    this.$events.$on('pay-overdue', (totalAmount) => {
      console.log('Pay overdue in Subscriptions component')
      console.log('Total amount ' + totalAmount)
    })
  },

  methods: {
    payOverdue() {
      this.showPayOverdueModal = false
      this.isLoading = true
      this.axios.post(`subscriptions/${this.activeSubscription.id}/overdue/pay`).then(({data}) => {
        if (data.payment_method === 'mobile_pay') {
          let redirectUrl = data.url

          if (process.env.NODE_ENV === 'development') {
            this.$router.push({
              name: 'mobile_pay_success',
              query: {
                id: this.activeSubscription.id,
                type: 'subscriptions',
                op: 'status'
              }
            })
          } else {
            window.location.href = redirectUrl
          }
        } else if (data.payment_method === 'vipps') {
          window.location.href = data.url
        } else {
          this.isLoading = false
          let subscription = data.model

          if (subscription.status === 'active') {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.success.success'),
              message: this.$t('globals.validation.success.overdue_payment_successful'),
              type: 'success',
              align: 'right'
            })

            this.$events.$emit('updateactivesubscription', subscription)
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.error'),
              message: this.$t('globals.validation.errors.overdue_payment_failed'),
              type: 'warning',
              align: 'right'
            })

            this.isOverdueChargedFailed = !this.isOverdueChargedFailed
            this.showUpdatePaymentCardModal = !this.showUpdatePaymentCardModal

            this.$events.$emit('updateactivesubscription', subscription)
          }
        }
      }).catch((error) => {
        this.isLoading = false
        if (error.response.data.message) {
          this.$events.$emit('alert', {
            header: this.$t('globals.validation.errors.error'),
            message: error.response.data.message,
            type: 'error',
            align: 'right'
          })
        }
        this.$events.$emit('updateactivesubscription')
      })
    },

    closeUpdatePaymentCardModal() {
      this.isOverdueChargedFailed = false
      this.showUpdatePaymentCardModal = false
    },

    payOverdueFee() {
      if (this.activeSubscription.payment_method === 'mobile_pay' || this.activeSubscription.payment_method === 'vipps') {
        this.axios.post(`subscriptions/${this.activeSubscription.id}/overdue/fee/payment`).then(({data}) => {
          if (data.success) {
            window.location.href = data.url
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.something_went_wrong'),
              message: this.$t('globals.validation.errors.try_again_later'),
              type: 'error',
              align: 'right'
            })

            this.showPayOverdueFeeModal = !this.showPayOverdueFeeModal
          }
        }).catch((error) => {
          if (error.response) {
            if (error.response.data.message) {
              this.$events.$emit('alert', {
                header: this.$t('globals.validation.errors.error'),
                message: error.response.data.message,
                type: 'error',
                align: 'right'
              })
            } else {
              this.$events.$emit('alert', {
                header: this.$t('globals.validation.errors.something_went_wrong'),
                message: this.$t('globals.validation.errors.try_again_later'),
                type: 'error',
                align: 'right'
              })
            }
          }

          this.showPayOverdueFeeModal = !this.showPayOverdueFeeModal
        })
      } else {
        if (this.activeSubscription.nets_details.is_unscheduled && this.activeSubscription.nets_details.unscheduled_subscription_id	!== null) {
          this.isLoading = true
          let data = {
            unscheduled: true
          }

          this.axios.post(`subscriptions/${this.activeSubscription.id}/overdue/fee/charge`, data).then(() => {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.success.success'),
              message: this.$t('globals.validation.success.overdue_fee_payment_successful'),
              type: 'success',
              align: 'right'
            })

            this.isLoading = false
            this.$events.$emit('updateactivesubscription', this.activeSubscription)
          }).catch(error => {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.something_went_wrong'),
              message: this.$t('globals.validation.errors.try_again_later'),
              type: 'error',
              align: 'right'
            })

            this.isLoading = false
            this.$events.$emit('updateactivesubscription', this.activeSubscription)
          })
        } else {
          this.$router.push({
            name: 'checkout_overdue_fee',
            params: {id: this.activeSubscription.id, container: 'subscriptions'}
          })
        }
      }
    },

    activatePricing() {
      this.showPricing = this.displayPricingAmount !== null
    },

    updatePaymentInformation() {
      if (this.activeSubscription.nets_details !== null) {
        let query = {
          container: 'subscription'
        }

        if (this.activeSubscription.nets_details.subscription_id) {
          query['subscriptionId'] = this.activeSubscription.nets_details.subscription_id
        } else if (this.activeSubscription.nets_details.unscheduled_subscription_id) {
          query['unscheduled'] = true
          query['unscheduledSubscriptionId'] = this.activeSubscription.nets_details.unscheduled_subscription_id
        }

        this.$router.push({
          name: 'checkout',
          params: {containerId: this.activeSubscription.id},
          query: query
        })
      } else {
        this.$events.$emit('alert', {
          header: this.$t('globals.validation.errors.something_went_wrong'),
          message: this.$t('globals.validation.errors.try_again_later'),
          type: 'error',
          align: 'right'
        })
      }
    },

    openAddPaymentMethodModal () {
      this.selectedPaymentMethod = this.activeSubscription.payment_method
      this.showAddPaymentMethodModal = true
    },

    addPaymentMethod () {
      if (!this.selectedPaymentMethod || this.isAddingPaymentMethod) {
        return
      }

      this.isAddingPaymentMethod = true
      this.axios.put(`subscriptions/${this.activeSubscription.id}`, {
        payment_method: this.selectedPaymentMethod
      }).then(() => {
        if (this.selectedPaymentMethod === 'vipps') {
          return this.axios.post(`subscriptions/${this.activeSubscription.id}/createPayment`, {
            setup_payment_method: true
          }).then(({data}) => {
            this.showAddPaymentMethodModal = false
            this.isAddingPaymentMethod = false
            window.location.href = data.url
          })
        }

        const checkoutRoute = {
          name: 'checkout',
          params: {containerId: this.activeSubscription.id},
          query: {
            container: 'subscription',
            setupPaymentMethod: true
          }
        }

        this.showAddPaymentMethodModal = false
        this.isAddingPaymentMethod = false
        this.$nextTick(() => {
          this.$router.push(checkoutRoute)
        })
      }).catch((error) => {
        this.isAddingPaymentMethod = false
        this.$events.$emit('alert', {
          header: this.$t('globals.validation.errors.error'),
          message: error.response && error.response.data.message
            ? error.response.data.message
            : this.$t('globals.validation.errors.try_again_later'),
          type: 'error',
          align: 'right'
        })
      })
    },

    setStartDate() {
      this.startDate = moment(this.activeSubscription.start_date).format('Do MMM, YYYY')
    },

    activateTransactions() {
      // Logic for showing transactions here
      if (this.finalWaterPeriodPayment) {
        this.showTransactions = false
      } else if (this.activeSubscription.status === 'active' && this.activeSubscription.pricing.type !== 'instant') {
        this.showTransactions = true
      } else if (this.activeSubscription.status === 'delayed' && this.activeSubscription.pricing && (this.activeSubscription.pricing.type === 'instant' || this.activeSubscription.pricing.type === 'recurring')) {
        if (this.activeSubscription.transactions.length === 0) {
          this.showTransactions = true
        } else {
          let successTransaction = this.activeSubscription.transactions.filter((transaction) => {
            return transaction.status === 'success'
          })

          this.showTransactions = successTransaction.length === 0
        }
      } else if (this.activeSubscription.pricing && this.activeSubscription.pricing.type === 'instant' && this.activeSubscription.transactions.length === 0) {
        this.showTransactions = true
      } else {
        this.showTransactions = false
      }

      if (!this.showTransactions) {
        this.fetchProcessedTransactions()
      }
    },

    fetchProcessedTransactions () {
      this.axios.get(`subscriptions/${this.activeSubscription.id}/transactions`, {
        params: {
          perPage: 3,
          page: 1
        }
      })
        .then(({data}) => {
          this.processedTransactions = data
        }).catch()
    },

    activatePaymentMethod () {
      // Logic for showing payment method here
      if (this.activeSubscription.nets_details !== null && this.activeSubscription.nets_details.payment_details) {
        this.showPaymentMethod = true
      }
      if (this.activeSubscription.is_free) {
        this.showPaymentMethod = true
      }
      if (this.activeSubscription.payment_method === 'mobile_pay') {
        this.showPaymentMethod = true
      }
      if (this.activeSubscription.payment_method === 'vipps') {
        this.showPaymentMethod = true
      }
    },

    activateAccessories() {
      this.showAccessories = this.activeSubscription.accessories !== 'null'
    },

    findActiveAccessories() {
      this.activeAccessories = []
      const accessories = JSON.parse(this.activeSubscription.accessories)
      const _this = this

      if (accessories !== null) {
        $.map(accessories, function (amount, accessoryId) {
          _this.activeAccessories.push(find(_this.activeSubscription.product.accessories, {id: parseInt(accessoryId)}))
        })
      }
    },

    cancelSubscription() {
      // Validation
      this.axios.delete(`subscriptions/${this.activeSubscription.id}`)
        .then(({data}) => {
          this.$events.$emit('updatesubscriptions', data, true)
        }).catch((error) => {
        if (error.response.data.message) {
          this.$events.$emit('alert', {
            header: this.$t('globals.validation.errors.error'),
            message: error.response.data.message,
            type: 'error',
            align: 'right'
          })
        }
      })
    },

    subscriptionDelayed() {
      this.isDelayedSubscription = this.activeSubscription.status === 'delayed'
    }
  },

  computed: {
    finalWaterPeriodPayment() {
      return this.activeSubscription.final_water_period_payment || null
    },

    displayPricingAmount() {
      if (this.finalWaterPeriodPayment && this.finalWaterPeriodPayment.amount !== null) {
        return this.finalWaterPeriodPayment.amount
      }

      return this.activeSubscription.water_period_price
        ? this.activeSubscription.water_period_price.amount
        : this.activeSubscription.pricing_amount
    },

    canBeCancelled() {
      if (this.activeSubscription.is_free) {
        return false
      }

      if (this.activeSubscription.pricing.type === 'instant') {
        let successTransaction = this.activeSubscription.transactions.filter((transaction) => {
          return transaction.status === 'success' || transaction.status === 'pending' || transaction.status === 'refunded'
        })

        if ((this.activeSubscription.status === 'delayed' || this.activeSubscription.status === 'active') && successTransaction.length === 0) {
          return true
        }
      }

      return (this.activeSubscription.status === 'active' || this.activeSubscription.status === 'delayed') && this.activeSubscription.pricing.type !== 'instant'
    },

    canChangePaymentMethod() {
      if (this.activeSubscription.is_free || this.activeSubscription.is_manual || this.finalWaterPeriodPayment) {
        return false
      }

      if (this.activeSubscription.nets_details !== null) {
        if (this.activeSubscription.status === 'active' || this.activeSubscription.status === 'delayed' || this.activeSubscription.status === 'overdue') {
          return !!this.activeSubscription.nets_details.subscription_id || !!this.activeSubscription.nets_details.unscheduled_subscription_id
        }
      }

      return false
    },

    canAddPaymentMethod () {
      if (this.activeSubscription.is_manual || this.finalWaterPeriodPayment) {
        return false
      }

      if (!this.activeSubscription.pricing || this.activeSubscription.pricing.type !== 'recurring') {
        return false
      }

      if (this.activeSubscription.product.category.water_meter) {
        return false
      }

      if (!['active', 'delayed', 'overdue', 'soft_overdue'].includes(this.activeSubscription.status)) {
        return false
      }

      if (this.activeSubscription.payment_method === 'nets') {
        const netsDetails = this.activeSubscription.nets_details

        if (!netsDetails) {
          return true
        }

        const usesUnscheduledAgreement = this.activeSubscription.product.unscheduled ||
          netsDetails.is_unscheduled
        const agreementId = usesUnscheduledAgreement
          ? netsDetails.unscheduled_subscription_id
          : netsDetails.subscription_id

        return !netsDetails.payment_id || !agreementId
      }

      if (this.activeSubscription.payment_method === 'vipps') {
        return !this.activeSubscription.vipps_setup
      }

      return !this.activeSubscription.payment_method
    },

    availablePaymentMethods () {
      const methods = [
        {id: 1, name: 'Nets', slug: 'nets'}
      ]

      if (this.$store.getters.activeMerchant.vipps) {
        methods.push({id: 2, name: 'Vipps/MobilePay', slug: 'vipps'})
      }

      const fixedPaymentMethod = this.activeSubscription.product.payment_method ||
        this.activeSubscription.payment_method

      if (fixedPaymentMethod) {
        return methods.filter(method => method.slug === fixedPaymentMethod)
      }

      return methods
    },

    canPayOverdueFee() {
      if (this.activeSubscription.status === 'overdue' &&
        this.activeSubscription.product.overdue_fee_amount !== null &&
        this.activeSubscription.apply_overdue_fee) {
        if (this.activeSubscription.payment_method === 'mobile_pay') {
          return this.$store.getters.activeMerchant.vipps
        } else {
          return true
        }
      }

      return false
    },

    canPayOverdue() {
      if (this.activeSubscription.status === 'overdue') {
        if (this.activeSubscription.payment_method === 'mobile_pay') {
          return this.$store.getters.activeMerchant.vipps
        } else {
          return true
        }
      }

      return false
    },

    capUpdatePaymentCard() {

    },

    isButtonDisabled() {
      return this.$store.getters.activeMerchant && this.$store.getters.activeMerchant.opus && !this.$auth.user().social_number
    },

    meterReadingNotice() {
      if (!this.activeSubscription.kamstrup_meters[0].is_paid) {
        if (this.activeSubscription.kamstrup_meters[0].meter_reading_total > 0) {
          if (this.activeSubscription.status === 'overdue') {
            return this.$t('pages.dashboard.details.kamstrup.payment.overdue');
          } else {
            return this.$t('pages.dashboard.details.kamstrup.payment.upcoming');
          }
        } else {
          return this.$t('pages.dashboard.details.kamstrup.payment.no_updated_info');
        }
      } else {
        return this.$t('pages.dashboard.details.kamstrup.payment.paid');
      }
    }
  }
}
</script>

<style scoped>
.delayed-block, .water_meter_block {
  display: inline-block;
}

.water_meter_block .water_meter_notice {
  color: rgb(243, 156, 18);
  font-size: 16px;
}

.water_meter_block .water_meter_notice.overdue {
  color: #db2828;
}

.activation-date {
  color: green;
}

.delayed {
  color: #f39c12;
}

.modal-body-text {
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #dce6f2;
  border-left: 4px solid #2185d0;
  border-radius: 6px;
  background: #f8fafc;
  color: #334155;
  line-height: 1.5;
}

.modal-body-text__lead {
  margin: 0 0 8px;
  font-weight: 600;
}

.modal-body-text__note {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.modal-body-text__icon {
  margin-top: 2px;
  color: #f39c12;
}

@media screen and (max-width: 768px) {
  .change-payment-button {
    margin: 10px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/dashboard/Subscriptions.vue